import MuiAutocompleteField, {
  type AutocompleteProps,
} from '@mui/material/Autocomplete';
import { forwardRef } from 'react';
import TextField, { type InputProps } from './TextInput';

export type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

export type AutocompleteOption = {
  label: string;
  value: string | number;
};

type AutocompleteInputProps<T extends AutocompleteOption = AutocompleteOption> =
  Omit<
    AutocompleteProps<T, false, false, false>,
    'renderInput' | 'value' | 'options' | 'onChange'
  > & {
    value?: T['value'] | null;
    options: T[];
    onChange: (value: T['value'] | null) => void;
    error?: boolean;
    required?: boolean;
    helperText?: React.ReactNode;
    inputSlotProps?: InputProps;
    label?: React.ReactNode;
    placeholder: string;
    /**
     * Names the field for assistive tech when there is no visible `label`.
     * A placeholder is not a name -- it is gone as soon as anything is typed.
     */
    'aria-label'?: string;
  };

function AutocompleteInputInner<T extends AutocompleteOption>(
  props: AutocompleteInputProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    value,
    options,
    onChange,
    error,
    helperText,
    inputSlotProps,
    label,
    placeholder,
    required,
    disabled,
    sx,
    'aria-label': ariaLabel,
    ...restProps
  } = props;
  const selected = options.find((o) => o.value === value) ?? null;
  const fieldOnChange = (_: React.SyntheticEvent, option: T | null) => {
    const selectedValue = option ? option.value : null;
    onChange(selectedValue);
  };

  return (
    <MuiAutocompleteField
      ref={ref}
      value={selected}
      options={options}
      onChange={fieldOnChange}
      fullWidth
      disabled={disabled}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          helperText={helperText}
          error={error}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            },
            input: { ...params.slotProps.input, ...inputSlotProps },
            inputLabel: params.slotProps.inputLabel,
          }}
        />
      )}
      {...restProps}
    />
  );
}

const AutocompleteInput = forwardRef(AutocompleteInputInner) as <
  T extends AutocompleteOption = AutocompleteOption,
>(
  props: AutocompleteInputProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

export default AutocompleteInput;
